import React, { useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera } from 'lucide-react';
import PasswordInput from '@/components/PasswordInput';
import HeadshotCropper from '@/components/HeadshotCropper';
import { COUNTRY_OPTIONS, REGION_OPTIONS, SECTOR_OPTIONS, FORUM_TOPIC_OPTIONS } from '@/components/profile/profileConstants';
import { BEHAVIOURAL_QUESTIONS } from '@/utils/behavioural';
import useProfileOptions from '@/hooks/useProfileOptions';

const Field = ({ label, children }) => (
  <div><Label className="text-xs font-semibold text-slate-600">{label} <span className="text-red-500">*</span></Label><div className="mt-1.5">{children}</div></div>
);
const inputCls = 'h-11 border-slate-200';

const Text = ({ v, set, k, label, type = 'text', placeholder }) => (
  <Field label={label}>
    <Input data-testid={`setup-${k}-input`} type={type} value={v[k] || ''} placeholder={placeholder}
      onChange={e => set(k, e.target.value)} className={inputCls} />
  </Field>
);

const Pick = ({ v, set, k, label, options }) => (
  <Field label={label}>
    <Select value={v[k] || undefined} onValueChange={val => set(k, val)}>
      <SelectTrigger data-testid={`setup-${k}-select`} className="h-11"><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
      <SelectContent>{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  </Field>
);

export const AccountStep = ({ v, set, mode, terms }) => (
  <div className="space-y-4">
    <Text v={v} set={set} k="name" label="Full Name" />
    {mode === 'signup' ? (
      <>
        <Text v={v} set={set} k="email" label="Email" type="email" />
        <Field label="Password">
          <PasswordInput data-testid="setup-password-input" value={v.password || ''} onChange={e => set('password', e.target.value)} className={`${inputCls} pr-10`} />
        </Field>
        <div className="flex items-start gap-2">
          <Checkbox id="setup-terms" data-testid="terms-checkbox" checked={terms.agreed} onCheckedChange={terms.setAgreed} className="mt-0.5" />
          <label htmlFor="setup-terms" className="text-xs text-slate-500 leading-relaxed cursor-pointer">
            I agree to the <button type="button" onClick={terms.open} className="text-brand-navy underline">Terms & Conditions</button>, including the Privacy Policy and GDPR data processing agreement.
          </label>
        </div>
      </>
    ) : (
      <Field label="Email"><Input value={v.email || ''} disabled className={`${inputCls} bg-slate-50`} /></Field>
    )}
  </div>
);

export const RoleStep = ({ v, set }) => {
  const opts = useProfileOptions();
  return (
    <div className="space-y-4">
      <Text v={v} set={set} k="role" label="Job Title" placeholder="e.g. Head of Sustainability" />
      <Text v={v} set={set} k="organisation" label="Company" placeholder="Company name" />
      <Pick v={v} set={set} k="background" label="Stakeholder Type" options={opts.stakeholder_type || []} />
      <Pick v={v} set={set} k="sector" label="Sector" options={SECTOR_OPTIONS} />
    </div>
  );
};

export const LocationStep = ({ v, set }) => (
  <div className="space-y-4">
    <Pick v={v} set={set} k="country" label="Country" options={COUNTRY_OPTIONS} />
    <Text v={v} set={set} k="location" label="City" placeholder="e.g. London" />
    <Pick v={v} set={set} k="region" label="Region" options={REGION_OPTIONS} />
  </div>
);

export const HeadshotStep = ({ photoUrl, onPhoto }) => {
  const [file, setFile] = useState(null);
  const ref = useRef(null);
  return (
    <div className="flex flex-col items-center text-center gap-4 py-2">
      <button type="button" onClick={() => ref.current?.click()} data-testid="setup-headshot-btn"
        className="w-36 h-36 rounded-full border-2 border-dashed border-slate-300 hover:border-brand-navy bg-slate-50 overflow-hidden flex items-center justify-center transition-colors">
        {photoUrl ? <img src={photoUrl} alt="Your headshot" className="w-full h-full object-cover" data-testid="setup-headshot-preview" />
          : <Camera className="h-10 w-10 text-slate-300" />}
      </button>
      <p className="text-sm text-slate-500">{photoUrl ? 'Tap the photo to change it' : 'Add a clear photo of your face so members can recognise you'}</p>
      <input ref={ref} type="file" accept="image/*" className="hidden" data-testid="setup-headshot-input"
        onChange={e => { if (e.target.files?.[0]) setFile(e.target.files[0]); e.target.value = ''; }} />
      <HeadshotCropper file={file} open={!!file} onClose={() => setFile(null)} onSave={blob => { onPhoto(blob); setFile(null); }} />
    </div>
  );
};

export const TopicsStep = ({ v, set }) => {
  const list = v.forum_topics_of_interest || [];
  return (
    <div className="flex flex-wrap gap-2" data-testid="setup-topics">
      {FORUM_TOPIC_OPTIONS.map(t => {
        const on = list.includes(t);
        return (
          <button key={t} type="button" data-testid={`setup-topic-${t.replace(/\W+/g, '-').toLowerCase()}`}
            onClick={() => set('forum_topics_of_interest', on ? list.filter(x => x !== t) : [...list, t])}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-600 border-slate-200 hover:border-brand-navy/40'}`}>
            {t}
          </button>
        );
      })}
    </div>
  );
};

export const BehaviouralStep = ({ v, set }) => (
  <div className="space-y-5">
    {BEHAVIOURAL_QUESTIONS.map(({ key, q, options }) => (
      <div key={key} data-testid={`setup-${key}`}>
        <p className="text-sm font-medium text-slate-700 mb-2">{q} <span className="text-red-500">*</span></p>
        <div className="grid sm:grid-cols-2 gap-2">
          {options.map(([val, label]) => (
            <button key={val} type="button" onClick={() => set(key, val)} data-testid={`setup-${key}-${val}`}
              className={`text-left text-sm p-3 rounded-lg border transition-colors ${v[key] === val ? 'border-brand-navy bg-brand-navy/5 text-brand-navy font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
    ))}
  </div>
);
